/*
 * MathGraph32 Javascript : Software for animating online dynamic mathematics figures
 * http://www.mathgraph32.org/
 * @Author Yves Biton (yves.biton@sesamath.net)
 * @License: GNU AGPLv3 https://www.gnu.org/licenses/agpl-3.0.html
 */
const Color = require('../types/Color')
const Point = require('../types/Point')
const StyleTrait = require('../types/StyleTrait')
const Vect = require('../types/Vect')
const kernel = require('../kernel/kernel')
const CPointLie = require('./CPointLie')
const CSegment = require('./CSegment')
const CPointBase = require('./CPointBase')
module.exports = CPointLieLigne

/**
 * Point lié à une ligne brisée ou un polygone.
 * @constructor
 * @extends CPointLie
 * @param {CListeObjets} listeProprietaire : La liste propriétaire de l'objet
 * @param {CImplementationProto} impProto : null ou pointe sur la construction propriétaire de l'objet.
 * @param {Boolean} estElementFinal : true si l'objet est un objet final de construction
 * @param {Color} couleur : La couleur de l'objet
 * @param {Boolean} nomMasque : true si le nom de l'objet est masqué
 * @param {Integer} dec_x : Decalage horizontal du nom
 * @param {Integer} dec_y : Decalage vertical du nom
 * @param {Boolean} masque : true si l'objet est masque
 * @param {String} nom : Le nom eventuel de l'objet
 * @param {Integer} tailleNom : Indice de la taille du nom (voir mtg32.Fonte)
 * @param {mtg32.MotifPoint} motif : Le motif du point
 * @param {Boolean} marquePourTrace : true si le point est marqué pour la trace
 * @param {Boolean} pointLibre : true si non punaisé
 * @param {Number} abscisse : L'abscisse sur la ligne ou le polygone
 * @param {type} ligneLiee : La ligne brisée ou polygone auquel le point est lié
 * @returns {undefined}
 */
function CPointLieLigne (listeProprietaire, impProto, estElementFinal, couleur, nomMasque,
  dec_x, dec_y, masque, nom, tailleNom, motif, marquePourTrace, pointLibre, abscisse, ligneLiee) {
  if (arguments.length === 1) CPointLie.call(this, listeProprietaire);
  else {
    CPointLie.call(this, listeProprietaire, impProto, estElementFinal, couleur, nomMasque,
      dec_x, dec_y, masque, nom, tailleNom, motif, marquePourTrace, pointLibre, abscisse);
    this.ligneLiee = ligneLiee;
    if (listeProprietaire.className !== "CPrototype") this.tableauLongueurs = new Array(ligneLiee.colPoints.length-1);
  }
  this.point1 = new CPointBase(null);
  this.point2 = new CPointBase(null);
  this.seg = new CSegment(listeProprietaire, null, false, new Color(0,0,0), true,
    StyleTrait.traitFin, this.point1, this.point2);
  this.pointres = new Point();

}
CPointLieLigne.prototype = new CPointLie();
CPointLieLigne.prototype.constructor = CPointLieLigne;
CPointLieLigne.prototype.superClass = "CPointLie";
CPointLieLigne.prototype.className = "CPointLieLigne";


CPointLieLigne.prototype.getClone = function(listeSource, listeCible) {
  var ind1 = listeSource.indexOf(this.ligneLiee);
  var ind2 = listeSource.indexOf(this.impProto);
  return new CPointLieLigne(listeCible, listeCible.get(ind2, "CImplementationProto"),
    this.estElementFinal, this.couleur, this.nomMasque, this.dec_x, this.dec_y, this.masque, this.nom,
    this.tailleNom, this.motif, this.marquePourTrace, this.pointLibre, this.abscisse, listeCible.get(ind1, "CLignePolygonaleAncetre"));
};

CPointLieLigne.prototype.setClone = function(ptel) {
  CPointLie.prototype.setClone.call(this, ptel);
  // Il faut copier les valeurs du tableau de longueurs et la longueur totale
  this.longueurTotale = ptel.longueurTotale;
  for (var i = 0; i < this.tableauLongueurs.length- 1; i++) {
    this.tableauLongueurs[i] = ptel.tableauLongueurs[i];
  }
};

CPointLieLigne.prototype.ajouteAntecedents = function(liste) {
  liste.add(this.ligneLiee);
};

CPointLieLigne.prototype.confonduAvec = function(p) {
  var res = CPointLie.prototype.confonduAvec.call(this, p);
  if (!res) return false;
  return (this.ligneLiee === p.ligneLiee);
};

CPointLieLigne.prototype.dependDe = function(p) {
  if (this.elementTestePourDependDe === p) return this.dependDeElementTeste;
  return this.memoriseDependance(CPointLie.prototype.dependDe.call(this, p) || this.ligneLiee.dependDe(p));
};

CPointLieLigne.prototype.dependDePourBoucle = function(p) {
  return CPointLie.prototype.dependDePourBoucle.call(this, p) || this.ligneLiee.dependDePourBoucle(p);
};

CPointLieLigne.prototype.positionne = function(infoRandom, dimfen) {
  var i;

  this.existe = this.ligneLiee.existe;
  if (!this.existe) return;
  this.calculeLongueurs();
  if (this.longueurTotale === 0) {
    this.existe = false;
    return;
  }
  // On cherche maintenant sur quel segment est situé le point lié
  // Java : a été remanié par rapport au C++
  var longueurPartielle = 0;
  var longueurJusqueDebSegment = 0;
  var nbSommets = this.ligneLiee.colPoints.length;
  for (i = 0; i < nbSommets - 1; i++) {
    longueurJusqueDebSegment = longueurPartielle;
    longueurPartielle += this.tableauLongueurs[i];
    if (longueurPartielle/this.longueurTotale >= this.abscisse) break;
  }
  // Juste par précaution
  if (i === nbSommets-1) i = nbSommets - 2;
  var n1 = this.ligneLiee.colPoints[i];
  var ptp1 = n1.pointeurSurPoint;
  var n2 = this.ligneLiee.colPoints[i + 1];
  var ptp2 = n2.pointeurSurPoint;
  var abscisseSurSegment;
  if (this.tableauLongueurs[i] === 0) abscisseSurSegment = 0;
  else abscisseSurSegment = (this.longueurTotale*this.abscisse-longueurJusqueDebSegment)/this.tableauLongueurs[i];
  var xn = ptp1.x + (abscisseSurSegment)*(ptp2.x - ptp1.x);
  var yn = ptp1.y + (abscisseSurSegment)*(ptp2.y - ptp1.y);
  CPointLie.prototype.placeEn.call(this, xn,yn);
  CPointLie.prototype.positionne.call(this, infoRandom, dimfen);
};
/**
 * Renvoie l'abscisse minimale de la ligne auquel le point est lié
 * @returns {Number}
 */
CPointLieLigne.prototype.abscisseMinimale = function() {
  return this.ligneLiee.abscisseMinimale();
};
/**
 * Renvoie l'ordonnée minimale de la ligne auquel le point est lié
 * @returns {Number}
 */
CPointLieLigne.prototype.abscisseMaximale = function() {
  return this.ligneLiee.abscisseMaximale();
};
/**
 * Fonction calculant les longueurs des côtés de la ligne brisée ou du polygone
 * et les mettant dans this.tableauLongueurs.
 * La longueur totale de laligen est mise dans this.longueurTotale.
 * @returns {undefined}
 */
CPointLieLigne.prototype.calculeLongueurs = function() {
  this.longueurTotale = 0;
  for (var i = 0; i < this.ligneLiee.colPoints.length - 1; i++) {
    var n1 = this.ligneLiee.colPoints[i];
    var n2 = this.ligneLiee.colPoints[i + 1];

    var ptp1 = n1.pointeurSurPoint;
    var ptp2 = n2.pointeurSurPoint;
    var u = new Vect(ptp1,ptp2);
    var longueurSegment = u.norme();
    this.tableauLongueurs[i] = longueurSegment;
    this.longueurTotale += longueurSegment;
  }
};

CPointLieLigne.prototype.testDeplacement = function(dimfen, xtest, ytest, pointr, abscr) {
  var ptPointProche = null;
  var distMin = 2147483647;
  // On calcule les longueurs des côtés et la longueur totale
  // de la ligne polygonale cliquée
  this.calculeLongueurs();

  var indiceDistMin = -1;
  var valable = false;
  for (var i = 0; i < this.ligneLiee.colPoints.length - 1; i++) {
    var n1 = this.ligneLiee.colPoints[i];
    var n2 = this.ligneLiee.colPoints[i + 1];

    var ptp1 = n1.pointeurSurPoint;
    var ptp2 = n2.pointeurSurPoint;
    this.point1.placeEn(ptp1.x, ptp1.y);
    this.point1.positionne(false, dimfen); // Spécial JavaScript
    this.point2.placeEn(ptp2.x, ptp2.y);
    this.point2.positionne(false, dimfen); // Spécial JavaScript
    this.seg.positionne();
    kernel.projetteOrtho(xtest,ytest,ptp1.x,ptp1.y,this.seg.vect,this.pointres);
    if (this.seg.appartientA(this.pointres.x,this.pointres.y)) {
      valable = true;
      var u = new Vect(this.pointres.x, this.pointres.y, xtest, ytest);
      var dist = u.norme();
      if (dist < distMin) {
        ptPointProche = ptp1;
        distMin = dist;
        indiceDistMin = i;
        pointr.x = this.pointres.x;
        pointr.y = this.pointres.y;
      }
    }
    else {
      var d1 = kernel.distancePointPoint(ptp1.x, ptp1.y, xtest, ytest);
      if (d1 < distMin) {
        valable = true;
        ptPointProche = ptp1;
        distMin = d1;
        indiceDistMin = i;
        pointr.x = ptp1.x;
        pointr.y = ptp1.y;
      }
    }
  }
  if (valable) {
    // Il faut calculer l'abscisse du point
    var longueurDebutLigne = 0;
    for (var j = 0; j < indiceDistMin; j ++) {
      longueurDebutLigne += this.tableauLongueurs[j];
    }
    var v = new Vect(ptPointProche.x, ptPointProche.y, pointr.x, pointr.y);
    longueurDebutLigne += v.norme();
    abscr.setValue(longueurDebutLigne/this.longueurTotale);
  }
  return valable;
};

CPointLieLigne.prototype.read = function(inps, list){
  CPointLie.prototype.read.call(this, inps, list);
  var ind1 = inps.readInt();
  this.ligneLiee = list.get(ind1,"CLignePolygonaleAncetre");
  // Pas de réservation en mémoire pour le chargement des prototypes
  if (list.className !== "CPrototype") this.tableauLongueurs = new Array(this.ligneLiee.colPoints.length - 1);
};

CPointLieLigne.prototype.write = function(oups, list){
  CPointLie.prototype.write.call(this, oups, list);
  var ind1 = list.indexOf(this.ligneLiee);
  oups.writeInt(ind1);
};




// WEBPACK FOOTER //
// ./src/objets/CPointLieLigne.js